import $ from 'jquery';
import Masonry from 'masonry-layout';
import GeneralHelper from '../../00_utilities/helpers/GeneralHelper';
import VideoHelper from '../../00_utilities/helpers/VideoHelper';
import BreakpointHelper from '../../00_utilities/helpers/BreakpointHelper';
import ScrollToHelper from '../../00_utilities/helpers/ScrollToHelper';

class Accordion {
    constructor(elem) {
        this.elem = elem;
        this.toggles = this.elem.querySelectorAll('.accordion__toggle');
        this.noImpactOnBigScreens = this.elem.classList.contains('accordion--no-impact-on-big-screens');
        this.breakpoints = {
            'screen-sm': 768,
        };
        this.aboveBreakpoint = BreakpointHelper.aboveBreakpoint(this.breakpoints['screen-sm']);
    }

    init() {
        this.handleResize();

        this.initializeAccordionItems();

        if (this.elem.classList.contains('accordion--to-masonry')) {
            this.initMasonry();
        }

        if (this.elem.classList.contains('accordion--first-open')) {
            this.openFirstItem();
        }
    }

    /**
     * add resize handling (recalculate item heights and handle breakpoint crossing)
     */
    handleResize() {
        if (this.noImpactOnBigScreens) {
            this.handleBreakpoint();

            this.boundResizeListener = this.handleBreakpointCrossing.bind(this);
            this.throttledResizeListener = GeneralHelper.throttle(this.boundResizeListener, 200);
            window.addEventListener('resize', this.throttledResizeListener);
        }
    }

    handleBreakpointCrossing() {
        const breakpointInfo = BreakpointHelper.breakpointCrossed(
            this.breakpoints['screen-sm'],
            this.aboveBreakpoint,
        );
        this.aboveBreakpoint = breakpointInfo.aboveBreakpoint;
        const { breakpointCrossed } = breakpointInfo;

        if (breakpointCrossed) {
            this.handleBreakpoint();
        }
    }

    handleBreakpoint() {
        if (!this.aboveBreakpoint) {
            for (const toggle of this.toggles) {
                const itemContent = toggle.parentNode.querySelector('.accordion__collapse');
                Accordion.setAccordionLabels(toggle, itemContent);
                toggle.removeAttribute('tabindex');
            }
        }
        if (this.aboveBreakpoint) {
            this.elem.querySelectorAll('[aria-hidden]').forEach((element) => {
                element.removeAttribute('aria-hidden');
            });

            for (const toggle of this.toggles) {
                Accordion.setAccordionLabels(toggle, toggle.parentNode.querySelector(('.accordion__collapse')));
                toggle.setAttribute('tabindex', -1);
            }
        }
    }

    /**
     * change accessibility labels according to the element's state
     */
    initializeAccordionItems() {
        for (const toggle of this.toggles) {
            const collapse = toggle.parentNode.querySelector('.accordion__collapse');

            Accordion.setAccordionLabels(toggle, collapse);

            toggle.addEventListener('change', (e) => {
                const toggleInput = e.currentTarget;
                if (!this.noImpactOnBigScreens) {
                    Accordion.scrollToOpenedItem(toggleInput);
                }

                const toggleItem = e.currentTarget;
                const collapseItem = toggleItem.parentNode.querySelector('.accordion__collapse');
                Accordion.setAccordionLabels(toggle, collapseItem);

                this.closeOthersOnOpen(e);
            });
        }
    }

    /**
     * set initial values for accessibility labels so they
     * will only show in the code in case js is enabled
     */
    static setAccordionLabels(toggle, collapse) {
        if (!toggle.hasAttribute('role')) {
            toggle.setAttribute('role', 'button');
        }

        if (Accordion.isOpen(toggle)) {
            toggle.setAttribute('aria-expanded', 'false');
            toggle.setAttribute('aria-pressed', 'false');
            collapse.setAttribute('aria-hidden', 'true');

            return;
        }

        toggle.setAttribute('aria-pressed', 'true');
        toggle.setAttribute('aria-expanded', 'true');
        collapse.setAttribute('aria-hidden', 'false');
    }

    /**
     * check if accordion is open by checking the trigger state
     * @param accordionToggle
     * @returns {*}
     */
    static isOpen(accordionToggle) {
        return accordionToggle.checked;
    }

    /**
     * close other accordions if a new one is triggered
     */
    closeOthersOnOpen(e) {
        if (Accordion.isOpen(e.currentTarget)) {
            const $accordionToggle = $(e.currentTarget).parent().find('.accordion__toggle');
            const $accordionCollapse = $(e.currentTarget).parent().find('.accordion__collapse');

            VideoHelper.stopAll();

            $(e.currentTarget).parent().siblings().find('.accordion__toggle')
                .prop('checked', false);
            $accordionToggle.attr('aria-expanded', 'false');
            $accordionCollapse.attr('aria-hidden', 'true');
        }
    }

    initMasonry() {
        let layoutDesktop = window.matchMedia(`(min-width: ${this.breakpoints['screen-sm']}px)`).matches;
        let masonry = null;

        if (layoutDesktop) {
            masonry = this.initMasonryObject('.accordion__item');
        }

        $(document).on('lazyloaded', () => {
            if (layoutDesktop && (masonry !== null)) {
                masonry.layout();
            }
        });

        window.addEventListener('resize orientationchange', GeneralHelper.throttle(() => {
            layoutDesktop = window.matchMedia(`(min-width: ${this.breakpoints['screen-sm']}px)`).matches;
            if (layoutDesktop) {
                if (!masonry) {
                    masonry = this.initMasonryObject('.accordion__item');
                }

                masonry.layout();
                return;
            }

            if (masonry) {
                masonry.destroy();
                masonry = null;
            }
        }, 200));
    }

    initMasonryObject(selector) {
        return new Masonry(this.elem, {
            itemSelector: selector,
            layoutMode: 'masonry',
            sortBy: 'accordion__subtitle-text',
        });
    }

    openFirstItem() {
        const firstItem = this.elem.querySelector('.accordion__item');
        const toggle = firstItem.querySelector('.accordion__toggle');

        toggle.click();
    }

    static prevAll(element) {
        const prevElements = [];
        let prevElement = element.parentNode.firstElementChild;

        while (prevElement !== element) {
            prevElements.push(prevElement);
            prevElement = prevElement.nextElementSibling;
        }

        return prevElements;
    }

    static getPreviousOpenedSibling(item) {
        const prevSiblings = Accordion.prevAll(item.closest('.accordion__item'));
        let checkedSiblingHeight = 0;

        if (prevSiblings.length) {
            for (const prevSibling of prevSiblings) {
                const checkedToggle = prevSibling.querySelector('.accordion__toggle:checked');
                if (checkedToggle) {
                    checkedSiblingHeight = prevSibling.querySelector('.accordion__collapse').offsetHeight;
                }
            }
        }

        return checkedSiblingHeight ? parseInt(checkedSiblingHeight, 10) : 0;
    }

    static scrollToOpenedItem(toggleInput) {
        const checkedSiblingHeight = Accordion.getPreviousOpenedSibling(toggleInput);
        const accordionItem = toggleInput.closest('.accordion__item');
        let offset = ScrollToHelper.getElementTopOffset(toggleInput);

        if (Accordion.isOpen(toggleInput)) {
            const headerHeight = ScrollToHelper.getHeaderHeight();
            if (checkedSiblingHeight > 0) {
                offset -= checkedSiblingHeight + headerHeight;

                window.scrollTo({
                    behavior: 'smooth',
                    left: 0,
                    top: offset,
                });

                return;
            }
            ScrollToHelper.scrollTo(accordionItem, headerHeight);
        }
    }
}

export default Accordion;
